/**
 * @file StatementDeliveryLog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-02
 *
 * One statement's delivery log — every send, resend, print and save, with the
 * provider's delivery outcome — and the "resent" mark that replaces a failure
 * flag once a later email went out. Shared by the contact modal's Statements
 * panel and the statement run history, which read the same `deliveries` array
 * (see utils/statementDelivery.js).
 */
import React from 'react';
import { resentTitle } from '../utils/statementDelivery';
import './StatementsPanel.css';

export const DELIVERY_ACTION_META = {
  customer: { icon: 'mail', label: 'Customer' },
  office: { icon: 'business', label: 'Office' },
  save: { icon: 'download', label: 'Saved' },
  print: { icon: 'print', label: 'Printed' }
};

/** Status words where the raw value would not read well. */
const STATUS_LABELS = {
  print_unconfirmed: 'not confirmed',
  print_pending: 'awaiting printer'
};

const STATUS_TITLES = {
  print_unconfirmed: 'Sent to the printer, but the printer did not confirm it printed — see the error column',
  print_pending: 'Sent to the printer and not confirmed yet: the print is still running, or it stopped before the printer answered'
};

/** Shown in place of the failure flag once a failed email has been resent. */
export function ResentFlag({ outcome }) {
  if (!outcome?.resent) return null;
  const title = resentTitle(outcome);
  return (
    // role="img" so a screen reader reads the label, not the ligature text.
    <span className="material-symbols-rounded sp-resent" role="img" title={title} aria-label={title}>forward_to_inbox</span>
  );
}

function StatementDeliveryLog({ deliveries }) {
  const list = Array.isArray(deliveries) ? deliveries : [];
  if (list.length === 0) return <div className="sp-muted">No delivery actions recorded.</div>;
  return (
    <table className="sp-deliv-table">
      <thead>
        <tr><th>When</th><th>Action</th><th>Recipient</th><th>Status</th><th>Delivery</th><th>By</th><th>SMTP / error</th></tr>
      </thead>
      <tbody>
        {list.map((d, i) => (
          <tr key={d.id ?? i}>
            <td>{d.ts || '—'}</td>
            <td>{(DELIVERY_ACTION_META[d.recipient_type] || {}).label || d.recipient_type || '—'}</td>
            <td>{d.email || '—'}</td>
            <td><span className={`sp-status sp-${d.status}`}
              title={STATUS_TITLES[d.status]}>
              {STATUS_LABELS[d.status] || d.status}</span></td>
            <td>{d.delivery_status
              ? <span className={`sp-status sp-deliv-${d.delivery_status}`}
                  title={d.delivery_at ? `${d.delivery_status} · ${d.delivery_at}` : d.delivery_status}>{d.delivery_status}</span>
              : <span className="sp-muted">—</span>}</td>
            <td>{d.by_name
              ? <span className="sp-by">{d.by_name}{d.by_is_admin != null && <span className="sp-role-badge">{d.by_is_admin ? 'ADMIN' : 'USER'}</span>}</span>
              : '—'}</td>
            <td className="sp-smtp">{d.error_message || d.smtp_response || (d.message_id ? `id ${d.message_id}` : '—')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default StatementDeliveryLog;
